/**
 * Shared lookup for designer-owned reference tables.
 *
 * The app uses two: material_categories (0019) and budget_categories
 * (Phase 17) — the same shape: rows the designer owns, referenced from
 * elsewhere by a nullable id, rendered by name. A third, room_types (0017),
 * was removed from the product in 0027; its table remains only as that
 * migration's rollback path.
 *
 * Resolving an id to a name is one line, but it's one line with two traps that
 * have both bitten here: a missing id must render as nothing rather than the
 * string "undefined", and an id can dangle when the row is deleted in another
 * tab. Defining that once beats re-deriving it per table.
 */

export interface NamedRow {
  id: string;
  name: string;
}

export interface Lookup<T extends NamedRow> {
  /** Display name for an id, or '' when unset or unknown. */
  name(id: string | null | undefined): string;
  /** The row itself, or null. */
  get(id: string | null | undefined): T | null;
  /** All rows, in the order supplied. */
  all: T[];
}

export function indexByName<T extends NamedRow>(rows: T[]): Lookup<T> {
  const byId = new Map(rows.map((r) => [r.id, r]));
  return {
    // '' rather than a placeholder: these references are nullable by design,
    // and a row without one should render nothing at all.
    name: (id) => (id ? byId.get(id)?.name ?? '' : ''),
    get: (id) => (id ? byId.get(id) ?? null : null),
    all: rows,
  };
}

/** Rows as `<select>` options. The VALUE is always the id, never the name. */
export function toOptions<T extends NamedRow>(rows: T[]): { value: string; label: string }[] {
  return rows.map((r) => ({ value: r.id, label: r.name }));
}
